import { FormattedMessage } from 'react-intl';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';

import {
  FIELD_AMOUNT,
  FIELD_GROUP_BUTTONS,
  FIELD_MEMBERS,
  FIELD_MEMBER_SELECTED,
  FIELD_YES_NO,
  LARGE_GRID_PROPS,
  SMALL_GRID_PROPS,
  COMMON_FIELD_NAMES,
} from './constants.fields';
import { ADDRESS_FIELDS } from 'pages/AffordaWagesAndIncome/constants.job';

export const MSG_REAL_ESTATE = 'afforda.assets.details.realEstate';

const ASSET_TYPE = {
  HOUSE: 'house',
  LAND: 'land',
  MOBILE_HOME: 'mobile_home',
  OTHER: 'other',
};

export const ASSET_PLANNING = {
  SELLING: 'selling',
  RENTING: 'renting',
  NEITHER: 'neither',
};

const ASSET_TYPE_OPTIONS = {
  HOUSE: {
    value: ASSET_TYPE.HOUSE,
    text: (
      <FormattedMessage id="afforda.assets.details.realEstate.type.house" />
    ),
  },
  LAND: {
    value: ASSET_TYPE.LAND,
    text: <FormattedMessage id="afforda.assets.details.realEstate.type.land" />,
  },
  MOBILE_HOME: {
    value: ASSET_TYPE.MOBILE_HOME,
    text: (
      <FormattedMessage id="afforda.assets.details.realEstate.type.mobileHome" />
    ),
  },
  OTHER: {
    value: ASSET_TYPE.OTHER,
    text: (
      <FormattedMessage id="afforda.assets.details.realEstate.type.other" />
    ),
  },
};

const ASSET_PLANNING_OPTIONS = {
  SELLING: {
    value: ASSET_PLANNING.SELLING,
    text: (
      <FormattedMessage id="afforda.assets.details.realEstate.planning.selling" />
    ),
  },
  RENTING: {
    value: ASSET_PLANNING.RENTING,
    text: (
      <FormattedMessage id="afforda.assets.details.realEstate.planning.renting" />
    ),
  },
  NEITHER: {
    value: ASSET_PLANNING.NEITHER,
    text: (
      <FormattedMessage id="afforda.assets.details.realEstate.planning.neither" />
    ),
  },
};

export const REAL_ESTATE_FIELD_NAMES = {
  amount: COMMON_FIELD_NAMES.amount,
  assetType: 'assetType',
  costToSell: 'costToSell',
  feesToConvert: COMMON_FIELD_NAMES.feesToConvert,
  interestPortion: 'interestPortion',
  interestRate: COMMON_FIELD_NAMES.interestRate,
  liveIn: 'liveIn',
  marketValue: 'marketValue',
  memberId: COMMON_FIELD_NAMES.memberId,
  mortgageBalance: 'mortgageBalance',
  planning: 'planning',
  rentReceived: 'rentReceived',
  taxes: 'taxes',
  type: COMMON_FIELD_NAMES.type,
};

export const FIELDS = ({
  intl,
  members,
  disabledMembers,
  states,
  validateZip,
}) => {
  return [
    ...FIELD_MEMBERS({ members, disabledMembers }),
    // Type of Asset currently Owned
    {
      ...FIELD_MEMBER_SELECTED,
      ...FIELD_GROUP_BUTTONS,
      label: <FormattedMessage id="afforda.assets.details.realEstate.type" />,
      name: REAL_ESTATE_FIELD_NAMES.type,
      options: Object.values(ASSET_TYPE_OPTIONS)?.map((option) => ({
        value: option.value,
        label: option.text,
      })),
    },
    // Type of Asset currently Owned
    {
      ...FIELD_MEMBER_SELECTED,
      label: <FormattedMessage id="afforda.assets.details.realEstate.type" />,
      name: REAL_ESTATE_FIELD_NAMES.assetType,
      hidden: ({ getValues }) =>
        getValues(REAL_ESTATE_FIELD_NAMES.type) !== ASSET_TYPE.OTHER,
      watchFields: [REAL_ESTATE_FIELD_NAMES.type],
      GridProps: LARGE_GRID_PROPS,
      rules: {
        required: true,
      },
    },

    // Can you live at this property?
    {
      ...FIELD_MEMBER_SELECTED,
      ...FIELD_YES_NO,
      label: <FormattedMessage id="afforda.assets.details.realEstate.liveIn" />,
      name: REAL_ESTATE_FIELD_NAMES.liveIn,
    },
    // Property Street Address
    ...ADDRESS_FIELDS({ intl, states, validateZip, ...FIELD_MEMBER_SELECTED }),
    // Planning to sell / have sold or will you rent it out?
    {
      ...FIELD_MEMBER_SELECTED,
      ...FIELD_GROUP_BUTTONS,
      label: (
        <FormattedMessage id="afforda.assets.details.realEstate.planning" />
      ),
      name: REAL_ESTATE_FIELD_NAMES.planning,
      options: Object.values(ASSET_PLANNING_OPTIONS)?.map((option) => ({
        value: option.value,
        label: option.text,
      })),
    },

    // Market Value of Asset
    {
      ...FIELD_MEMBER_SELECTED,
      ...FIELD_AMOUNT,
      label: (
        <FormattedMessage id="afforda.assets.details.realEstate.marketValue" />
      ),
      name: REAL_ESTATE_FIELD_NAMES.marketValue,
      rules: {
        required: true,
        max: 499999.99,
        min: 0,
        step: 0.01,
      },
      GridProps: SMALL_GRID_PROPS,
      watchFields: [REAL_ESTATE_FIELD_NAMES.planning],
      hidden: ({ getValues }) =>
        getValues(REAL_ESTATE_FIELD_NAMES.planning) !==
          ASSET_PLANNING.SELLING &&
        getValues(REAL_ESTATE_FIELD_NAMES.planning) !== ASSET_PLANNING.NEITHER,
      InputLabelProps: {
        TooltipProps: {
          title: (
            <FormattedMessage id="afforda.assets.details.realEstate.marketValue.tooltip" />
          ),
          children: <AlertInfoIcon fontSize={'small'} />,
        },
      },
    },
    // Outstanding Mortgage Balance.
    {
      ...FIELD_MEMBER_SELECTED,
      ...FIELD_AMOUNT,
      label: (
        <FormattedMessage id="afforda.assets.details.realEstate.mortgageBalance" />
      ),
      name: REAL_ESTATE_FIELD_NAMES.mortgageBalance,
      rules: {
        required: true,
        max: 499999.99,
        min: 0,
        step: 0.01,
      },
      GridProps: SMALL_GRID_PROPS,
      watchFields: [REAL_ESTATE_FIELD_NAMES.planning],
      hidden: ({ getValues }) =>
        getValues(REAL_ESTATE_FIELD_NAMES.planning) !==
          ASSET_PLANNING.SELLING &&
        getValues(REAL_ESTATE_FIELD_NAMES.planning) !== ASSET_PLANNING.NEITHER,
    },
    // Cost to Sell - Realtor Fees
    {
      ...FIELD_MEMBER_SELECTED,
      ...FIELD_AMOUNT,
      label: (
        <FormattedMessage id="afforda.assets.details.realEstate.costToSell" />
      ),
      name: REAL_ESTATE_FIELD_NAMES.costToSell,
      rules: {
        required: true,
        max: 499999.99,
        min: 0,
        step: 0.01,
      },
      GridProps: SMALL_GRID_PROPS,
      watchFields: [REAL_ESTATE_FIELD_NAMES.planning],
      hidden: ({ getValues }) =>
        getValues(REAL_ESTATE_FIELD_NAMES.planning) !== ASSET_PLANNING.SELLING,
      InputLabelProps: {
        TooltipProps: {
          title: (
            <FormattedMessage id="afforda.assets.details.realEstate.costToSell.tooltip" />
          ),
          children: <AlertInfoIcon fontSize={'small'} />,
        },
      },
    },
    // Rent Received per Month
    {
      ...FIELD_MEMBER_SELECTED,
      ...FIELD_AMOUNT,
      label: (
        <FormattedMessage id="afforda.assets.details.realEstate.rentReceived" />
      ),
      name: REAL_ESTATE_FIELD_NAMES.rentReceived,
      rules: {
        required: true,
        max: 499999.99,
        min: 0,
        step: 0.01,
      },
      GridProps: SMALL_GRID_PROPS,
      watchFields: [REAL_ESTATE_FIELD_NAMES.planning],
      hidden: ({ getValues }) =>
        getValues(REAL_ESTATE_FIELD_NAMES.planning) !== ASSET_PLANNING.RENTING,
    },
    // Interest Portion Paid on Mortgage.
    {
      ...FIELD_MEMBER_SELECTED,
      ...FIELD_AMOUNT,
      label: (
        <FormattedMessage id="afforda.assets.details.realEstate.interestPortion" />
      ),
      name: REAL_ESTATE_FIELD_NAMES.interestPortion,
      rules: {
        required: true,
        max: 499999.99,
        min: 0,
        step: 0.01,
      },
      GridProps: SMALL_GRID_PROPS,
      watchFields: [REAL_ESTATE_FIELD_NAMES.planning],
      hidden: ({ getValues }) =>
        getValues(REAL_ESTATE_FIELD_NAMES.planning) !== ASSET_PLANNING.RENTING,
    },
    // Taxes Paid per Month
    {
      ...FIELD_MEMBER_SELECTED,
      ...FIELD_AMOUNT,
      label: <FormattedMessage id="afforda.assets.details.realEstate.taxes" />,
      name: REAL_ESTATE_FIELD_NAMES.taxes,
      rules: {
        required: true,
        max: 499999.99,
        min: 0,
        step: 0.01,
      },
      GridProps: SMALL_GRID_PROPS,
      watchFields: [REAL_ESTATE_FIELD_NAMES.planning],
      hidden: ({ getValues }) =>
        getValues(REAL_ESTATE_FIELD_NAMES.planning) !== ASSET_PLANNING.RENTING,
    },
  ];
};
